import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";

export default function RecentSearchesSection() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    const fetchRecent = async () => {
      setLoading(true);
      setError("");
      try {
        const parsed = JSON.parse(localStorage.getItem("recently_viewed_properties") || "[]");
        const list = Array.isArray(parsed) ? parsed : [];
        if (list.length === 0) {
          if (live) {
            setProperties([]);
            setLoading(false);
          }
          return;
        }

        // Fetch up to 15 entries so we can filter out nulls/deleted properties and still show 3
        let failedRequests = 0;
        const promises = list.slice(0, 15).map(id =>
          api.get(`/properties/${id}`)
            .then(res => res.data)
            .catch((err) => {
              if (err?.response?.status === 404) {
                try {
                  const current = JSON.parse(localStorage.getItem("recently_viewed_properties") || "[]");
                  const updated = current.filter(x => x !== id);
                  localStorage.setItem("recently_viewed_properties", JSON.stringify(updated));
                } catch {
                  // Discard malformed local-only history instead of failing on
                  // every homepage render.
                  localStorage.removeItem("recently_viewed_properties");
                }
              } else {
                failedRequests += 1;
              }
              return null;
            })
        );
        const results = await Promise.all(promises);
        if (live) {
          const validProps = results.filter(Boolean).slice(0, 3);
          setProperties(validProps);
          if (failedRequests > 0) {
            setError(validProps.length > 0
              ? "Some recently viewed listings are temporarily unavailable."
              : "Recently viewed listings could not be loaded.");
          }
        }
      } catch (e) {
        console.error("Error fetching recent searches for section:", e);
        if (live) {
          localStorage.removeItem("recently_viewed_properties");
          setProperties([]);
          setError("Recently viewed listings could not be loaded.");
        }
      } finally {
        if (live) {
          setLoading(false);
        }
      }
    };

    fetchRecent();
    return () => { live = false; };
  }, [reloadKey]);

  const handleRemoveRecent = (propertyId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const list = JSON.parse(localStorage.getItem("recently_viewed_properties") || "[]");
      const updated = list.filter((id) => id !== propertyId);
      localStorage.setItem("recently_viewed_properties", JSON.stringify(updated));
      setProperties(prev => prev.filter(p => (p.property_id || p.id) !== propertyId));
    } catch (e) {
      console.error("Error removing from history:", e);
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-white/40 border-b border-slate-200/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="h-8 w-48 shimmer rounded mb-8" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-96 rounded-2xl shimmer border border-slate-200" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (properties.length === 0 && !error) return null;

  return (
    <section className="py-16 bg-white/40 border-b border-slate-200/40" data-testid="recently-searched-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-12 relative z-10">
        {error && properties.length === 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" role="alert">
            <p className="font-bold text-slate-900">Recent listings are unavailable</p>
            <p className="mt-1 text-sm text-slate-600">{error}</p>
            <Button type="button" variant="outline" className="mt-4" onClick={() => setReloadKey((value) => value + 1)}>Try again</Button>
          </div>
        )}
        {error && properties.length > 0 && (
          <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between" role="status">
            <span>{error}</span>
            <Button type="button" size="sm" variant="outline" onClick={() => setReloadKey((value) => value + 1)}>Retry</Button>
          </div>
        )}
        {properties.length > 0 && <>
        {/* Header Row */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div className="gold-divider border-l-4 pl-4">
            <h2 className="font-display text-2xl font-black tracking-tight text-slate-900">
              Continue Where You Left Off
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Resume exploring properties you inspected recently.
            </p>
            {/* Mobile View All Link */}
            <div className="mt-2 sm:hidden">
              <Link 
                to="/recent-searches"
                className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-800 transition"
                data-testid="view-all-recent-link-mobile"
              >
                View All Recent Searches <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
          {/* Desktop/Tablet View All Link */}
          <Link 
            to="/recent-searches"
            className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-800 transition shrink-0"
            data-testid="view-all-recent-link"
          >
            View All Recent Searches <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Responsive Grid / Horizontal Carousel */}
        <div className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory flex-nowrap lg:grid lg:grid-cols-3 lg:overflow-visible pb-4 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0">
          {properties.map((p) => (
            <div 
              key={p.property_id || p.id} 
              className="relative group snap-center shrink-0 w-auto"
            >
              <PropertyCard 
                property={p} 
                isRecentMobile={true} 
                onRemoveRecent={(id, e) => handleRemoveRecent(id, e)} 
              />
              <button
                onClick={(e) => handleRemoveRecent(p.property_id || p.id, e)}
                className="hidden md:flex absolute top-3 right-16 z-20 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white items-center justify-center backdrop-blur-sm transition border border-white/10"
                title="Remove from history"
                data-testid={`remove-home-recent-${p.property_id || p.id}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        </>}
      </div>
    </section>
  );
}
